import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from './lib/queryClient'
import { useAuthStore } from './stores/authStore'
import ProtectedRoute from './components/auth/ProtectedRoute'
import AppShell from './components/layout/AppShell'
import ErrorBoundary from './components/ui/ErrorBoundary'
import ToastContainer from './components/ui/Toast'
import HomePage from './pages/HomePage'
import LoginPage from './pages/LoginPage'
import SignupPage from './pages/SignupPage'
import PicksPage from './pages/PicksPage'
import ResultsPage from './pages/ResultsPage'
import LeaderboardPage from './pages/LeaderboardPage'
import HubPage from './pages/HubPage'
import SettingsPage from './pages/SettingsPage'
import LeaguesPage from './pages/LeaguesPage'
import CreateLeaguePage from './pages/CreateLeaguePage'
import JoinLeaguePage from './pages/JoinLeaguePage'
import MockDraftPage from './pages/MockDraftPage'
import DraftPrepPage from './pages/DraftPrepPage'
import LeagueDetailPage from './pages/LeagueDetailPage'
import AdminPage from './pages/AdminPage'
import WriterPage from './pages/WriterPage'
import SportScoresPage from './pages/SportScoresPage'
import PaymentPage from './pages/PaymentPage'
import JoinPage from './pages/JoinPage'
import PrivacyPage from './pages/PrivacyPage'
import DeleteAccountPage from './pages/DeleteAccountPage'
import TermsPage from './pages/TermsPage'
import FAQPage from './pages/FAQPage'
import UnsubscribePage from './pages/UnsubscribePage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import HallOfFamePage from './pages/HallOfFamePage'
import MessagesPage from './pages/MessagesPage'
import MessageThreadPage from './pages/MessageThreadPage'
import GuidelinesPage from './pages/GuidelinesPage'
import OnboardingTutorial from './components/onboarding/OnboardingTutorial'
import HeroLayout from './components/layouts/HeroLayout'
import InstallPrompt from './components/pwa/InstallPrompt'
import { initPushNotifications } from './lib/pushNotifications'
import { initDraftAudio } from './lib/draftAudio'
import { initStatusBar } from './lib/statusBar'
import { initIAPListener } from './lib/iapListener'
import { useRealtimeGames } from './hooks/useRealtimeGames'
import { useSeasonDatesPrimer } from './hooks/useSeasonDates'

function AppRoutes() {
  const initialize = useAuthStore((s) => s.initialize)
  const isAuthenticated = useAuthStore((s) => !!s.session)
  useRealtimeGames(isAuthenticated)
  // Prime react-query cache so the synchronous isSeasonUnderway /
  // arePlayoffsUnderway helpers can consult admin-defined dates.
  useSeasonDatesPrimer()

  useEffect(() => {
    initialize()
    initStatusBar()
    initIAPListener()
    initPushNotifications()
    // Binds the one-time gesture listener that unlocks Web Audio. Must be
    // armed before a draft starts — an AudioContext created later, inside a
    // timer, is born suspended on iOS and stays silent.
    initDraftAudio()
  }, [initialize])

  return (
    <AppShell>
      <Routes>
        {/* All entry/auth/payment routes share a persistent hero layout.
            The wordmark + cycling backdrop live in HeroLayout and don't
            remount when navigating between these routes, so the visual
            doesn't jump. For /join/:code the layout swaps to the league's
            own backdrop, which then persists through signup → payment. */}
        <Route element={<HeroLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/payment" element={<PaymentPage />} />
          <Route path="/join/:code" element={<JoinPage />} />
        </Route>
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/delete-account" element={<DeleteAccountPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/guidelines" element={<GuidelinesPage />} />
        <Route path="/unsubscribe" element={<UnsubscribePage />} />
        <Route path="/picks" element={<ProtectedRoute><PicksPage /></ProtectedRoute>} />
        <Route path="/results" element={<ProtectedRoute><ResultsPage /></ProtectedRoute>} />
        <Route path="/leaderboard" element={<ProtectedRoute><LeaderboardPage /></ProtectedRoute>} />
        {/* Scoreboard drill-in — public (no auth) so logged-out users can browse
            the same sport scores they see on the landing card. */}
        <Route path="/scores/:sport" element={<SportScoresPage />} />
        <Route path="/hub" element={<ProtectedRoute><HubPage /></ProtectedRoute>} />
        <Route path="/profile" element={<Navigate to="/hub" replace />} />
        <Route path="/connections" element={<Navigate to="/hub" replace />} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/leagues" element={<ProtectedRoute><LeaguesPage /></ProtectedRoute>} />
        <Route path="/leagues/create" element={<ProtectedRoute><CreateLeaguePage /></ProtectedRoute>} />
        <Route path="/leagues/join" element={<ProtectedRoute><JoinLeaguePage /></ProtectedRoute>} />
        <Route path="/leagues/draft-prep" element={<ProtectedRoute><DraftPrepPage /></ProtectedRoute>} />
        <Route path="/leagues/mock-draft" element={<Navigate to="/leagues/draft-prep?tab=mock-draft" replace />} />
        <Route path="/leagues/:id" element={<ProtectedRoute><LeagueDetailPage /></ProtectedRoute>} />
        <Route path="/hall-of-fame" element={<ProtectedRoute><HallOfFamePage /></ProtectedRoute>} />
        <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
        <Route path="/messages/:partnerId" element={<ProtectedRoute><MessageThreadPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
        <Route path="/writer" element={<ProtectedRoute><WriterPage /></ProtectedRoute>} />
      </Routes>
    </AppShell>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppRoutes />
          <OnboardingTutorial />
          <InstallPrompt />
          <ToastContainer />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
